// src/admin/AdminProperties.js
import { useEffect, useMemo, useState } from "react";
import { adminGet, adminPatch, adminDelete } from "./adminApi";
import "./Admin.css";

function locText(p) {
  if (!p) return "";
  if (typeof p.location === "string") return p.location;
  if (p.location?.text) return p.location.text;
  return "";
}

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function load() {
    setErr("");
    setLoading(true);
    try {
      const data = await adminGet(`/api/admin/properties?status=${encodeURIComponent(status)}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setItems([]);
      setErr(e?.message || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  }

  async function setStatusApi(id, nextStatus) {
    setErr("");
    try {
      await adminPatch(`/api/admin/properties/${id}/status`, { status: nextStatus });
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to update status");
    }
  }

  async function setHidden(id, isHidden) {
    setErr("");
    try {
      await adminPatch(`/api/admin/properties/${id}/visibility`, { isHidden });
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to update visibility");
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete property?")) return;
    setErr("");
    try {
      await adminDelete(`/api/properties/${id}`);
      await load();
    } catch (e) {
      setErr(e?.message || "Delete failed");
    }
  }

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items.filter((p) => {
      const title = String(p?.title || "").toLowerCase();
      const location = String(locText(p) || "").toLowerCase();
      const propertyStatus = String(p?.status || "pending").toLowerCase();
      return title.includes(q) || location.includes(q) || propertyStatus.includes(q);
    });
  }, [items, query]);

  function statusBadgeClass(st) {
    if (st === "active") return "admin-badge admin-badge--active";
    if (st === "rejected") return "admin-badge admin-badge--rejected";
    return "admin-badge admin-badge--pending";
  }

  return (
    <div className="admin-page">
      <div className="admin-page__top">
        <div>
          <h1 className="admin-page__title">Admin – Properties</h1>
          <p className="admin-page__subtitle">
            Manage property approval, visibility and moderation.
          </p>
        </div>

        <button className="admin-btn admin-btn--ghost" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filters">
          <button
            className={`admin-chip ${status === "pending" ? "admin-chip--active" : ""}`}
            onClick={() => setStatus("pending")}
            disabled={status === "pending"}
          >
            Pending
          </button>

          <button
            className={`admin-chip ${status === "active" ? "admin-chip--active" : ""}`}
            onClick={() => setStatus("active")}
            disabled={status === "active"}
          >
            Active
          </button>

          <button
            className={`admin-chip ${status === "rejected" ? "admin-chip--active" : ""}`}
            onClick={() => setStatus("rejected")}
            disabled={status === "rejected"}
          >
            Rejected
          </button>

          <button
            className={`admin-chip ${status === "all" ? "admin-chip--active" : ""}`}
            onClick={() => setStatus("all")}
            disabled={status === "all"}
          >
            All
          </button>
        </div>

        <input
          className="admin-search"
          type="text"
          placeholder="Search by title, location or status..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {err && <div className="admin-alert">{err}</div>}

      <div className="admin-statsRow">
        <div className="admin-statCard">
          <span className="admin-statCard__label">Current filter</span>
          <strong className="admin-statCard__value">{status}</strong>
        </div>

        <div className="admin-statCard">
          <span className="admin-statCard__label">Loaded properties</span>
          <strong className="admin-statCard__value">{items.length}</strong>
        </div>

        <div className="admin-statCard">
          <span className="admin-statCard__label">Visible after search</span>
          <strong className="admin-statCard__value">{filteredItems.length}</strong>
        </div>
      </div>

      <div className="admin-tableWrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th align="left">Title</th>
              <th align="left">Location</th>
              <th align="left">Status</th>
              <th align="left">Hidden</th>
              <th align="left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="5" className="admin-emptyCell">
                  {loading ? "Loading..." : "No results"}
                </td>
              </tr>
            ) : (
              filteredItems.map((p) => {
                const isHidden = Boolean(p?.isHidden);
                const st = (p?.status || "pending").toLowerCase();

                return (
                  <tr key={p._id}>
                    <td>
                      <div className="admin-propertyCell">
                        <strong>{p.title || "Untitled"}</strong>
                        {p?._id && <span className="admin-propertyId">ID: {p._id}</span>}
                      </div>
                    </td>

                    <td>{locText(p) || "-"}</td>

                    <td>
                      <span className={statusBadgeClass(st)}>{st}</span>
                    </td>

                    <td>
                      <span className={isHidden ? "admin-badge admin-badge--hidden" : "admin-badge admin-badge--visible"}>
                        {isHidden ? "yes" : "no"}
                      </span>
                    </td>

                    <td>
                      <div className="admin-actions">
                        {st === "pending" && (
                          <>
                            <button
                              className="admin-btn admin-btn--success"
                              onClick={() => setStatusApi(p._id, "active")}
                            >
                              Approve
                            </button>

                            <button
                              className="admin-btn admin-btn--dangerSoft"
                              onClick={() => setStatusApi(p._id, "rejected")}
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {st === "rejected" && (
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setStatusApi(p._id, "pending")}
                          >
                            Back to pending
                          </button>
                        )}

                        {st === "active" && (
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setStatusApi(p._id, "pending")}
                          >
                            Set pending
                          </button>
                        )}

                        {!isHidden ? (
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setHidden(p._id, true)}
                          >
                            Hide
                          </button>
                        ) : (
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setHidden(p._id, false)}
                          >
                            Unhide
                          </button>
                        )}

                        <button
                          className="admin-btn admin-btn--danger"
                          onClick={() => remove(p._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}